import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-59e2a1da"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 1,
  class: "route-planner-view"
}
const _hoisted_2 = { class: "left-panel-wrapper" }
const _hoisted_3 = { class: "route-planner-content-wrapper" }
const _hoisted_4 = { class: "route-planner-content-wrapper__map" }
const _hoisted_5 = { class: "route-planner-content-wrapper__widget" }

import _ from 'lodash'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import RouteMap from '@/components/public/route-planner/RouteMap.vue'
import BookingWidgetView from '@/views/public/BookingWidgetView.vue'
import {
  useAddStopMutation,
  useCreateRouteMutation,
  useDeleteStopMutation,
  useGetRouteMutation,
  useRoutePlanningServicesQuery,
  useUpdateDrivingSegmentsMutation,
  useUpdateRouteOrderMutation,
  useUpdateRouteStopsMutation,
} from '@/queries/public/services'
import { computed, onMounted, ref, watch } from 'vue'
import { PublicService } from '@/models/public/bookingWidget.model'
import RoutePanel from '@/components/public/route-planner/RoutePanel.vue'
import { Route, RouteStop } from '@/models/public/routePlanner.model'
import {
  calculateNewSegments,
  calculateServiceETA,
  parseRoute,
} from '@/utils/route-planner'
import { Order } from '@/models/public/order.model'
import { useRoute } from 'vue-router'


export default /*#__PURE__*/_defineComponent({
  __name: 'RoutePlannerView',
  setup(__props) {

const { query } = useRoute()
const { data: servicesData, isLoading } = useRoutePlanningServicesQuery()
const { mutateAsync: createRoute } = useCreateRouteMutation()
const { mutateAsync: getRoute } = useGetRouteMutation()
const { mutateAsync: createStop } = useAddStopMutation()
const { mutateAsync: deleteStop } = useDeleteStopMutation()
const { mutateAsync: updateSegments } = useUpdateDrivingSegmentsMutation()
const { mutateAsync: updateRouteStops } = useUpdateRouteStopsMutation()
const { mutateAsync: updateRouteOrder } = useUpdateRouteOrderMutation()
const services = ref<PublicService[]>([])
const route = ref<Route | null>(null)
const selectedService = ref<PublicService | null>(null)
const serviceInfos = ref<Record<number, any>>({})

const debouncedSync = _.debounce(handleSyncStops, 1000)

const selectedServiceEarliestStart = computed(() =>
  calculateServiceETA(selectedService.value, route.value),
)
const selectedServiceStart = computed(
  () =>
    route.value?.stops.find(
      (stop) => stop.service.id === selectedService.value?.id,
    )?.start_time,
)
const selectedServiceEnd = computed(
  () =>
    route.value?.stops.find(
      (stop) => stop.service.id === selectedService.value?.id,
    )?.end_time,
)

onMounted(async () => {
  if (!query.routeHash) return

  try {
    const existingRoute = await getRoute(query.routeHash as string)
    if (existingRoute) {
      const parsedRoute = parseRoute(existingRoute)
      parsedRoute.start_time = new Date()
      route.value = parsedRoute
    }
  } catch (error) {
    console.error(error)
  }
})

watch(
  () => servicesData.value,
  () => {
    services.value = servicesData.value || []
  },
)

watch(
  () => route.value?.stops,
  (newStops, prevStops) => {
    if (!_.isEqual(newStops, prevStops)) {
      debouncedSync()
    }
  },
)

const handleAdd = async (service: PublicService) => {
  handleSelectService(service)
  if (!route.value) {
    route.value = await handleCreateRoute()
  }
  handleAddStop(service)
}

const handleDelete = async (stop: RouteStop) => {
  try {
    const updatedRoute = await deleteStop(stop)
    if (updatedRoute) {
      route.value = parseRoute(updatedRoute)
    }
  } catch (e) {
    console.error(e)
  }
}

const handleCreateRoute = async () => {
  try {
    return await createRoute()
  } catch (e) {
    console.error(e)
  }
  return null
}

async function handleSyncStops() {
  try {
    if (!route.value?.stops) return

    const updatedRoute = await updateRouteStops({
      route: route.value.id,
      stops: route.value.stops.map((stop) => ({
        id: stop.id,
        order_index: stop.order_index,
        start_time: stop.start_time ? stop.start_time : null,
        end_time: stop.end_time ? stop.end_time : null,
      })),
    })
    if (updatedRoute) {
      route.value = parseRoute(updatedRoute)
      setStopPropertiesFromServiceInfos()
    }
  } catch (e) {
    console.error(e)
  }
}

const handleAddStop = async (service: PublicService) => {
  try {
    if (!route.value) return

    const updatedRoute = await createStop({
      route: route.value.id,
      service: service.id,
      start_time: null,
      end_time: null,
    })

    if (updatedRoute) {
      route.value = parseRoute(updatedRoute)
      setStopPropertiesFromServiceInfos()
    }
  } catch (e) {
    console.error(e)
  }
}

const handleUpdateRoute = (value) => {
  route.value = parseRoute(value)
  serviceInfos.value = {}
}

const handleUpdateSegments = async (googleMapsRoute) => {
  try {
    if (!route.value) return

    const segments = calculateNewSegments(googleMapsRoute, route.value)
    const updatedRoute = await updateSegments({
      route: route.value.id,
      segments,
    })

    if (updatedRoute) {
      route.value = parseRoute(updatedRoute)
      setStopPropertiesFromServiceInfos()
    }
  } catch (e) {
    console.error(e)
  }
}

const handleOrderChange = async (order: Order) => {
  if (!route.value) return

  try {
    const updatedRoute = await updateRouteOrder({
      id: route.value.id,
      order: order.id!,
    })
    route.value = parseRoute(updatedRoute)
  } catch (e) {
    console.error(e)
  }
}

const handleSelectService = (
  newSelectedService: PublicService,
  shouldRefresh = false,
) => {
  if (shouldRefresh || selectedService.value?.id !== newSelectedService.id) {
    selectedService.value = newSelectedService
  }
}

const handleDateChange = (
  property: 'start_time' | 'end_time',
  newDate: Date | null,
) => {
  if (!selectedService.value) return

  if (!serviceInfos.value[selectedService.value.id]) {
    serviceInfos.value[selectedService.value.id] = { [property]: newDate }
  } else {
    serviceInfos.value[selectedService.value.id][property] = newDate
  }
  setStopPropertiesFromServiceInfos()
}

const setStopPropertiesFromServiceInfos = () => {
  if (!route.value) return

  route.value.stops = route.value.stops.map((stop) => {
    const serviceInfo = serviceInfos.value[stop.service.id]
    if (serviceInfo) {
      return {
        ...stop,
        start_time: serviceInfo.start_time,
        end_time: serviceInfo.end_time,
      }
    }
    return stop
  })
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    (_unref(isLoading))
      ? (_openBlock(), _createBlock(LoadingSpinner, { key: 0 }))
      : (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createVNode(RoutePanel, {
              services: services.value,
              route: route.value,
              onOnChange: _cache[0] || (_cache[0] = (value) => (services.value = value)),
              onOnSelect: _cache[1] || (_cache[1] = (value) => handleSelectService(value, false)),
              onOnRefresh: _cache[2] || (_cache[2] = (value) => handleSelectService(value, true)),
              onOnDelete: _cache[3] || (_cache[3] = (value) => handleDelete(value)),
              onOnRouteUpdate: _cache[4] || (_cache[4] = (value) => handleUpdateRoute(value))
            }, null, 8, ["services", "route"])
          ]),
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createVNode(RouteMap, {
                route: route.value,
                services: services.value,
                "selected-service": selectedService.value,
                onOnAdd: _cache[5] || (_cache[5] = (value) => handleAdd(value)),
                onOnUpdateSegments: _cache[6] || (_cache[6] = (value) => handleUpdateSegments(value))
              }, null, 8, ["route", "services", "selected-service"])
            ]),
            _createElementVNode("div", _hoisted_5, [
              _createVNode(BookingWidgetView, {
                service: selectedService.value,
                "earliest-start": _unref(selectedServiceEarliestStart),
                start: _unref(selectedServiceStart),
                end: _unref(selectedServiceEnd),
                onOnStartChange: _cache[7] || (_cache[7] = (value) => handleDateChange('start_time', value)),
                onOnEndChange: _cache[8] || (_cache[8] = (value) => handleDateChange('end_time', value)),
                onOnOrderChange: _cache[9] || (_cache[9] = (value) => handleOrderChange(value))
              }, null, 8, ["service", "earliest-start", "start", "end"])
            ])
          ])
        ]))
  ]))
}
}

})